// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CheckoutHeading from "@steiner/App/Components/Checkout/CheckoutHeading";
import XngLoadingOverlay from "@steiner/App/Components/Common/XngLoadingOverlay";
import XngZone from "@steiner/App/Components/Common/XngZone";
import Notify from "@steiner/App/Components/Notify";
import { xSiteMessage } from "@steiner/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface CheckoutReviewAndSubmitPageContainerStyles {
    loadingOverlay?: LoadingOverlayProps;
    // container?: GridContainerProps;
    headerGridItem?: GridItemProps;
    approverInfoGridItem?: GridItemProps;
    promoCodeAndCartTotalGridItem?: GridItemProps;
    promoCodeAndCartTotalContainer?: GridContainerProps;
    promoCodeGridItem?: GridItemProps;
    cartTotalGridItem?: GridItemProps;
    paymentDetailsAndCreditCardDetailsAndAddressGridItem?: GridItemProps;
    paymentDetailsAndCreditCardDetailsAndAddressContainer?: GridContainerProps;
    paymentDetailsGridItem?: GridItemProps;
    creditCardDetailsGridItem?: GridItemProps;
    creditCardAddressGridItem?: GridItemProps;
    shippingInfoAndProductListGridItem?: GridItemProps;
    shippingInfoAndProductListContainer?: GridContainerProps;
    hiddenShippingInfoAndProductListWide?: HiddenProps;
    shippingInfoGridItemWide?: GridItemProps;
    productListGridItemWide?: GridItemProps;
    hiddenShippingInfoAndProductListNarrow?: HiddenProps;
    shippingInfoGridItemNarrow?: GridItemProps;
    productListGridItemNarrow?: GridItemProps;
    actionButtonsBottomGridItem?: GridItemProps;
    /**
     * XNG-Changes
     */
    container?: InjectableCss;
    innerContainer?: InjectableCss;
    cartMessagesContainer?: InjectableCss;
    shippingBillingInformationContainer?: InjectableCss;
    shippingMethodContainer?: InjectableCss;
    paymentMethodContainer?: InjectableCss;
    promotionContainer?: InjectableCss;
    productListContainer?: InjectableCss;
    leftContainer?: InjectableCss;
    rightContainer?: InjectableCss;
    freightNotify?: TypographyPresentationProps;
}

export const pageContainerStyles: CheckoutReviewAndSubmitPageContainerStyles = {
    loadingOverlay: {
        css: css`
            width: 100%;
        `,
    },
    headerGridItem: { width: 12 },
    approverInfoGridItem: {
        width: 12,
        css: css`
            padding: 0 15px;
        `,
    },
    promoCodeAndCartTotalGridItem: {
        width: [12, 12, 6, 4, 4],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            order: 1;
                        `,
                        null,
                        css`
                            order: 2;
                        `,
                        null,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    promoCodeGridItem: { width: 12 },
    cartTotalGridItem: { width: 12 },
    paymentDetailsAndCreditCardDetailsAndAddressGridItem: {
        width: [12, 12, 6, 8, 8],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            order: 2;
                        `,
                        null,
                        css`
                            order: 1;
                        `,
                        null,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    paymentDetailsGridItem: { width: 12 },
    creditCardDetailsGridItem: { width: [12, 12, 12, 6, 6] },
    creditCardAddressGridItem: { width: [12, 12, 12, 6, 6] },
    shippingInfoAndProductListGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            order: 3;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    hiddenShippingInfoAndProductListWide: {
        below: "lg",
        css: css`
            width: 100%;
        `,
    },
    shippingInfoGridItemWide: {
        width: 12,
    },
    productListGridItemWide: {
        width: 12,
    },
    hiddenShippingInfoAndProductListNarrow: {
        above: "md",
        css: css`
            width: 100%;
        `,
    },
    shippingInfoGridItemNarrow: {
        width: 12,
    },
    productListGridItemNarrow: {
        width: 12,
    },
    actionButtonsBottomGridItem: {
        css: css`
            order: 4;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
        width: 12,
    },
};

const styles = pageContainerStyles;
const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    return {
        isPreloadingData:
            state.pages.orderConfirmation.isPreloadingData ||
            state.pages.checkoutShipping.isPreloadingData ||
            state.pages.checkoutReviewAndSubmit.isPreloadingData ||
            cartState.isLoading,
        isPlacingOrder: state.pages.checkoutReviewAndSubmit.isPlacingOrder,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const CheckoutReviewAndSubmitPageContainer: FC<Props> = ({ id, isPreloadingData, isPlacingOrder }) => {
    return (
        <XngLoadingOverlay
            id="CheckoutReviewAndSubmitPageContainer"
            {...styles.loadingOverlay}
            loading={isPreloadingData || isPlacingOrder}
        >
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.leftContainer}>
                    <CheckoutHeading id={id} />
                    <StyledWrapper {...styles.innerContainer}>
                        <StyledWrapper {...styles.cartMessagesContainer}>
                            <XngZone zoneName="ContentCartMessages" contentId={id} />
                            {xSiteMessage("STR_Checkout_Freight") && (
                                <Notify type="warning" fullWidth={true} {...styles.freightNotify}>
                                    {xSiteMessage("STR_Checkout_Freight")}
                                </Notify>
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.shippingBillingInformationContainer}>
                            <Zone zoneName="ContentShippingBillingInformation" contentId={id} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.shippingMethodContainer}>
                            <Zone zoneName="ContentShippingMethod" contentId={id} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.paymentMethodContainer}>
                            <Zone zoneName="ContentPaymentMethod" contentId={id} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.promotionContainer}>
                            <Zone zoneName="ContentPromotion" contentId={id} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.productListContainer}>
                            <Zone zoneName="ContentProductList" contentId={id} />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightContainer}>
                    <Zone zoneName="Content02" contentId={id} />
                </StyledWrapper>
            </StyledWrapper>
        </XngLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CheckoutReviewAndSubmitPageContainer),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Page Container",
    },
};

export default widgetModule;
